import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, History, Pencil, X } from "lucide-react";

const FIELDS = [
  { key: "base_rate", label: "Base rate", unit: "$" },
  { key: "per_mile", label: "Per mile", unit: "$/mi" },
  { key: "min_fare", label: "Minimum fare", unit: "$" },
  { key: "hourly_rate", label: "Hourly rate", unit: "$/hr" },
];
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const inputCls = "w-full bg-black border border-white/15 px-2 py-1.5 text-sm text-[#F5F2EB] focus:outline-none focus:border-[#D4AF37]";

/** Rates summary + inline editor + change log for one fleet class. */
export default function VehicleRates({ v, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const openEditor = () => {
    setDraft({ base_rate: v.base_rate, per_mile: v.per_mile, min_fare: v.min_fare, hourly_rate: v.hourly_rate, note: "" });
    setEditing(true);
  };

  const save = async () => {
    const body = { note: draft.note };
    for (const f of FIELDS) {
      const n = parseFloat(draft[f.key]);
      if (Number.isNaN(n)) { toast.error(`${f.label} must be a number`); return; }
      if (n !== Number(v[f.key])) body[f.key] = n;
    }
    if (Object.keys(body).length === 1) { setEditing(false); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/vehicles/${v.vehicle_id}`, body);
      toast.success(`${v.name} rates updated — live for new quotes`);
      onSaved(r.data);
      setEditing(false);
      if (showHistory) loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save rates"); }
    finally { setBusy(false); }
  };

  const loadHistory = () => api.get(`/vehicles/${v.vehicle_id}/rate-history`).then(r => setHistory(r.data.history)).catch(() => setHistory([]));
  useEffect(() => { if (showHistory && history === null) loadHistory(); }, [showHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mt-3 border-t border-white/5 pt-3" data-testid={`fleet-rates-${v.vehicle_id}`}>
      {!editing ? (
        <div className="flex items-start justify-between gap-2">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
            {FIELDS.map(f => (
              <div key={f.key} className="flex items-baseline gap-1.5">
                <span className="text-[#71717A] uppercase tracking-[0.14em] text-[9px]">{f.label}</span>
                <span className="text-[#F5F2EB] font-medium" data-testid={`fleet-${f.key}-${v.vehicle_id}`}>{money(v[f.key])}{f.unit !== "$" ? f.unit.slice(1) : ""}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-1 shrink-0">
            <button data-testid={`fleet-edit-rates-${v.vehicle_id}`} onClick={openEditor} title="Edit rates"
              className="w-8 h-8 border border-white/15 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] flex items-center justify-center"><Pencil size={13}/></button>
            <button data-testid={`fleet-history-${v.vehicle_id}`} onClick={() => setShowHistory(s => !s)} title="Pricing history"
              className={`w-8 h-8 border flex items-center justify-center ${showHistory ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/15 text-[#A1A1AA] hover:text-[#D4AF37]"}`}><History size={13}/></button>
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid={`fleet-rates-editor-${v.vehicle_id}`}>
          <div className="grid grid-cols-2 gap-2">
            {FIELDS.map(f => (
              <label key={f.key} className="block">
                <span className="text-[9px] uppercase tracking-[0.14em] text-[#71717A]">{f.label} ({f.unit})</span>
                <input data-testid={`fleet-input-${f.key}-${v.vehicle_id}`} type="number" step="0.01" min="0" className={inputCls}
                  value={draft[f.key]} onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}/>
              </label>
            ))}
          </div>
          <input data-testid={`fleet-input-note-${v.vehicle_id}`} className={inputCls} placeholder="Reason (optional) — e.g. fuel surcharge" maxLength={200}
            value={draft.note} onChange={e => setDraft(d => ({ ...d, note: e.target.value }))}/>
          <div className="flex gap-2">
            <button data-testid={`fleet-save-rates-${v.vehicle_id}`} disabled={busy} onClick={save} className="btn-gold !py-1.5 !px-3 text-[10px] disabled:opacity-40">{busy ? "Saving…" : "Save rates"}</button>
            <button data-testid={`fleet-cancel-rates-${v.vehicle_id}`} onClick={() => setEditing(false)} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border border-white/15 text-[#A1A1AA] inline-flex items-center gap-1"><X size={11}/> Cancel</button>
          </div>
        </div>
      )}

      {v.last_rate_change && !showHistory && (
        <div className="mt-2 text-[10px] text-[#71717A] inline-flex items-center gap-1.5" data-testid={`fleet-last-change-${v.vehicle_id}`}>
          <Clock size={10}/> {v.last_rate_change.label} {money(v.last_rate_change.old)} → {money(v.last_rate_change.new)} · {when(v.last_rate_change.changed_at)} · {v.last_rate_change.changed_by}
        </div>
      )}

      {showHistory && (
        <div className="mt-3 border border-white/10 bg-black/40 max-h-48 overflow-y-auto" data-testid={`fleet-history-list-${v.vehicle_id}`}>
          {history === null && <div className="p-3 text-[10px] text-[#71717A]">Loading…</div>}
          {history?.length === 0 && <div className="p-3 text-[10px] text-[#71717A]" data-testid={`fleet-history-empty-${v.vehicle_id}`}>No rate changes yet — these are the seeded rates.</div>}
          {history?.map((h, i) => (
            <div key={i} className="px-3 py-2 border-b border-white/5 last:border-b-0 text-[11px] flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <span className="text-[#F5F2EB]">{h.label}</span>
              <span className="text-[#A1A1AA] line-through">{money(h.old)}</span>
              <span className="text-[#D4AF37]">{money(h.new)}</span>
              <span className="text-[#71717A] ml-auto">{when(h.changed_at)} · {h.changed_by}</span>
              {h.note && <span className="w-full text-[10px] text-[#A1A1AA] italic">"{h.note}"</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
